import { useLazyGetCareOrdersQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { formattedDateForSubscription, logger, useSecuritiesContext } from '@talos/kyoko';
import { subWeeks } from 'date-fns';
import { isArray } from 'lodash-es';
import { useCallback } from 'react';
import { useOrgConfiguration } from '../../../../../providers';
import { useBaskets } from '../../../../../providers/BasketsProvider';
import { useFundDefinitions } from '../../../../../providers/FundDefinitionProvider';
import * as aladdin from './aladdin';
import type { CareOrderImportContext } from './types';
import { type UnqorkOrder, unqorkOrdersToImportedCareOrders } from './unqork';
import { useCareOrderImportParserFlag } from './useCareOrderImportParserFlag';

export type CareOrderParserInput = File | UnqorkOrder[];

export function useCareOrderImportParser() {
  const { securitiesBySymbol } = useSecuritiesContext();
  const [fetchCareOrders] = useLazyGetCareOrdersQuery();
  const { baskets } = useBaskets();
  const { fundsList } = useFundDefinitions();
  const parser = useCareOrderImportParserFlag();
  const { getConfig } = useOrgConfiguration();

  return useCallback(
    async (input: CareOrderParserInput) => {
      const careOrders = await fetchCareOrders({
        tag: 'useCareOrderImportParser',
        // fetchCareOrders just gets one page of data. To make sure the first page includes all recent COs, we just get the last week's worth.
        // The endpoint doesn't seem to support sorting by SubmitTime yet.
        StartDate: formattedDateForSubscription(subWeeks(new Date(), 1)),
      });

      if (careOrders.data == null) {
        throw new Error('Could not fetch existing care orders, please try again.');
      }

      const context: CareOrderImportContext = {
        securitiesBySymbol,
        allCareOrders: [...careOrders.data.values()],
        baskets,
        fundDefinitions: fundsList,
        getConfig,
      };

      switch (parser) {
        case 'aladdin':
          if (isArray(input)) {
            // will need a smarter mechanism here in the future probably
            logger.error(new Error(`Invalid argument passed to aladdin parser (array)`));
            return Promise.reject('Invalid argument to import parser, please contact support.');
          }
          return aladdin.parse(input, context);
        case 'unqork':
          if (!isArray(input)) {
            logger.error(new Error(`Invalid argument passed to unqork parser (file)`));
            return Promise.reject('Invalid argument to import parser, please contact support.');
          }
          return unqorkOrdersToImportedCareOrders(input, context);
        default:
          logger.error(new Error(`Unknown care order import parser: ${parser}`));
          return Promise.reject('Invalid parser, please contact support.');
      }
    },
    [parser, securitiesBySymbol, fetchCareOrders, baskets, fundsList, getConfig]
  );
}
